import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getBalanceRequest: ["payload"], // payload: { params }
  getBalanceSuccess: ["payload"], // payload: { balance }

  updateBalanceRequest: ["payload"], // payload: { params }
  updateBalanceSuccess: ["payload"], // payload: { newBalance }

  topUpBalanceRequest: ["payload"], // payload: { params }
  topUpBalanceSuccess: ["payload"], // payload: { newBalance }
  topUpBalanceFailure: ["payload"], // payload: { errors }
  firstTopUpBalanceRequest: ["payload"], // payload: { params }
  firstTopUpBalanceSuccess: ["payload"], // payload: { newBalance }
  firstTopUpBalanceFailure: ["payload"], // payload: { errors }
  resetAddFundsLoading: null,

  balanceFailure: ["payload"], // payload: { errors }

  balanceSetProp: ["key", "value"],

  getBalanceActivityRequest: ["payload"], // payload: { params }
  getBalanceActivitySuccess: ["payload"], // payload: { data }

  getBalanceActivityExportRequest: ["payload"], // payload: { data }
  getBalanceActivityExportSuccess: ["payload"], // payload: { url }
  getBalanceActivityExportFailure: ["payload"], // payload: { errors }

  getBalanceActivityFilterOptionsRequest: ["payload"], // payload: { params }
  getBalanceActivityFilterOptionsSuccess: ["payload"], // payload: { data }
  getBalanceActivityFilterOptionsFailure: ["payload"], // payload: { data }

  clearBalanceFilters: ["payload"],
  clearAdyenSessionData: null,

  logout: null
})

export const BalanceTypes = Types
export default Creators

/* ------------- Initial State ------------- */

const columns = [
  { field_id: "balance_transaction_timestamp", label: "Date", active: true },
  { field_id: "amount", label: "Amount", active: true },
  { field_id: "balance_before", label: "Balance Before", active: true },
  { field_id: "balance_after", label: "Balance After", active: true },
  { field_id: "card_last4", label: "Payment Method", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "status", label: "Status", active: true },
  { field_id: "subtype", label: "Type", active: true },
  { field_id: "source_id", label: "Source ID", active: true },
  { field_id: "source_type", label: "Payment Source", active: true }
]

export const INITIAL_STATE = Immutable({
  balance: {
    min_amount: 0,
    recommended: 0,
    amount: 0,
    calculated_recommended: 0,
    calculated_min_amount: 0
  },
  fetching: false,

  saveFetching: false,
  saveResult: null,

  addFundsLoading: false,

  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  defaultColumns: [...columns],
  columns: [...columns],

  filtersLastUpd: null,
  filters: {
    balance_transaction_timestamp: null,

    status: "all",
    subtype: "all",
    source_type: "all",

    source_id: [],
    business_account: [],
    card_last4: [],

    amount: { min: null, max: null, type: null }
  },

  fetchingFilterOptions: false,
  filterOptions: {
    status: [],
    subtype: [],
    source_type: []
  },

  visibleFilters: { status: true, subtype: true, business_account: true },

  exportFetching: false,
  exportResult: null,
  adyenSessionData: null
})

/* ------------- Selectors ------------- */
export const selectAreFiltersDefault = (state) =>
  isEqual(state.balance.filters, INITIAL_STATE.filters)

/* ------------- Functions for reducer cases ------------- */
const getBalanceRequest = (state) => state.merge({ fetching: true })
const getBalanceSuccess = (state, { payload: balance }) =>
  state.merge({
    fetching: false,
    balance
  })

const balanceFailure = (state) => state.merge({ fetching: false, saveFetching: false })

const updateBalanceRequest = (state) => state.merge({ saveFetching: true })
const updateBalanceSuccess = (state, { payload: balance }) => {
  return state.merge({
    saveFetching: false,
    balance: { ...state.balance, ...balance },
    saveResult: "balance_update"
  })
}

const resetAddFundsLoading = (state) => state.merge({ addFundsLoading: false })

const topUpBalanceRequest = (state) => state.merge({ addFundsLoading: true })
const topUpBalanceSuccess = (state, { payload: result }) => {
  const hasAdyen = result.provider === "adyen"

  return state.merge({
    saveResult: result?.message
      ? { type: "balance_top_up", message: result?.message }
      : "balance_top_up",
    addFundsLoading: false,
    adyenSessionData: hasAdyen ? result : null
  })
}
const topUpBalanceFailure = (state) =>
  state.merge({
    fetching: false,
    saveFetching: false,
    addFundsLoading: false
  })

export const balanceSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters"]?.includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

const getBalanceActivityRequest = (state) => state.merge({ fetching: true })
const getBalanceActivitySuccess = (state, { payload: result }) => {
  if (result.results) {
    return state.merge({ fetching: false, result })
  } else {
    return state.merge({
      fetching: false,
      result: {
        count: result?.length || 0,
        results: result
      }
    })
  }
}

const getBalanceActivityExportRequest = (state) => state.merge({ exportFetching: true })
const getBalanceActivityExportSuccess = (state, { payload: exportResult }) =>
  state.merge({
    exportFetching: false,
    exportResult
  })
const getBalanceActivityExportFailure = (state) => state.merge({ exportFetching: false })

const getBalanceActivityFilterOptionsRequest = (state) =>
  state.merge({ fetchingFilterOptions: true })
const getBalanceActivityFilterOptionsSuccess = (state, { payload: filterOptions }) =>
  state.merge({ fetchingFilterOptions: false, filterOptions })
const getBalanceActivityFilterOptionsFailure = (state) =>
  state.merge({ fetchingFilterOptions: false })

export const clearBalanceFilters = (state) => {
  return state.merge({ filters: INITIAL_STATE.filters })
}
const clearAdyenSessionData = (state) => {
  return state.merge({ adyenSessionData: null })
}
const logout = (state) => INITIAL_STATE

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_BALANCE_REQUEST]: getBalanceRequest,
  [Types.GET_BALANCE_SUCCESS]: getBalanceSuccess,

  [Types.UPDATE_BALANCE_REQUEST]: updateBalanceRequest,
  [Types.UPDATE_BALANCE_SUCCESS]: updateBalanceSuccess,

  [Types.RESET_ADD_FUNDS_LOADING]: resetAddFundsLoading,
  [Types.TOP_UP_BALANCE_REQUEST]: topUpBalanceRequest,
  [Types.TOP_UP_BALANCE_SUCCESS]: topUpBalanceSuccess,
  [Types.TOP_UP_BALANCE_FAILURE]: topUpBalanceFailure,
  [Types.FIRST_TOP_UP_BALANCE_REQUEST]: topUpBalanceRequest,
  [Types.FIRST_TOP_UP_BALANCE_SUCCESS]: topUpBalanceSuccess,
  [Types.FIRST_TOP_UP_BALANCE_FAILURE]: topUpBalanceFailure,

  [Types.BALANCE_FAILURE]: balanceFailure,

  [Types.BALANCE_SET_PROP]: balanceSetProp,

  [Types.GET_BALANCE_ACTIVITY_REQUEST]: getBalanceActivityRequest,
  [Types.GET_BALANCE_ACTIVITY_SUCCESS]: getBalanceActivitySuccess,

  [Types.GET_BALANCE_ACTIVITY_EXPORT_REQUEST]: getBalanceActivityExportRequest,
  [Types.GET_BALANCE_ACTIVITY_EXPORT_SUCCESS]: getBalanceActivityExportSuccess,
  [Types.GET_BALANCE_ACTIVITY_EXPORT_FAILURE]: getBalanceActivityExportFailure,

  [Types.GET_BALANCE_ACTIVITY_FILTER_OPTIONS_REQUEST]: getBalanceActivityFilterOptionsRequest,
  [Types.GET_BALANCE_ACTIVITY_FILTER_OPTIONS_SUCCESS]: getBalanceActivityFilterOptionsSuccess,
  [Types.GET_BALANCE_ACTIVITY_FILTER_OPTIONS_FAILURE]: getBalanceActivityFilterOptionsFailure,

  [Types.CLEAR_BALANCE_FILTERS]: clearBalanceFilters,
  [Types.CLEAR_ADYEN_SESSION_DATA]: clearAdyenSessionData,

  [Types.LOGOUT]: logout
})
